import { useMemo, useState } from "react";
import {
  HiCheck,
  HiChevronDown,
  HiChevronRight,
  HiOutlineClipboardList,
  HiOutlineInformationCircle,
  HiOutlineViewGrid,
} from "react-icons/hi";
import "./CardQueryDescription.css";

/**
 * CardQueryDescription - layout clean em duas laminas
 * - Lamina esquerda: formulario
 * - Lamina direita: resumo do produto (descricao / tags) em card simples
 * - Adicionais: bloco full-width abaixo, agrupado por categoria
 * - Cabecalho minimo com kicker e titulo
 */
export default function CardQueryDescription({
  title,
  subtitle = "",
  infos = [],
  description = "",
  additionalOptions = [],
  selectedAdditionalIds = [],
  onChangeAdditionalIds = () => {},
  logoOverride,
  kicker = "Consulta",
  children,
  tags = [],
}) {
  const hasAdds =
    Array.isArray(additionalOptions) && additionalOptions.length > 0;
  const hasDescription = Boolean((description || "").trim());
  const hasTags = Array.isArray(tags) && tags.length > 0;

  const selectedSet = useMemo(
    () => new Set((selectedAdditionalIds || []).map(String)),
    [selectedAdditionalIds],
  );

  return (
    <section className="cqd2">
      <div className="cqd2__grid">
        {/* Form */}
        <section className="cqd2-card">
          <div className="cqd2-card__header">
            <HiOutlineClipboardList />
            <span>Consulta: {title}</span>
          </div>
          <div className="cqd2-card__body">
            <div className="cqd2__form">{children}</div>
          </div>
        </section>

        {/* Resumo */}
        <SummaryCard
          title={title}
          description={description}
          tags={tags}
          hasDescription={hasDescription}
          hasTags={hasTags}
        />
      </div>

      {hasAdds && (
        <InlineAddons
          options={additionalOptions}
          selectedSet={selectedSet}
          selectedAdditionalIds={selectedAdditionalIds}
          onChangeAdditionalIds={onChangeAdditionalIds}
        />
      )}
    </section>
  );
}

const getAddonCategory = (opt) => {
  const label =
    opt?.queryCategory ||
    opt?.category ||
    opt?.categoryName ||
    opt?.Category?.name ||
    opt?.meta?.category ||
    "Sem categoria";
  return String(label).trim() || "Sem categoria";
};

function InlineAddons({
  options = [],
  selectedSet: externalSelectedSet,
  selectedAdditionalIds = [],
  onChangeAdditionalIds = () => {},
}) {
  const selectedSet = useMemo(() => {
    if (externalSelectedSet && typeof externalSelectedSet.has === "function")
      return externalSelectedSet;
    return new Set((selectedAdditionalIds || []).map(String));
  }, [externalSelectedSet, selectedAdditionalIds]);

  const items = useMemo(
    () =>
      (options || []).map((opt) => ({
        id: opt?.id,
        name: opt?.name || "",
        category: getAddonCategory(opt),
        description: opt?.description || "",
        longDescription: opt?.meta?.longDescription || opt?.description || "",
        price: opt?.price,
        disabled: Boolean(opt?.disabled),
      })),
    [options],
  );

  const groupedItems = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const category = item.category || "Sem categoria";
      if (!map.has(category)) map.set(category, []);
      map.get(category).push(item);
    });

    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b, "pt-BR", { sensitivity: "base" }))
      .map(([category, groupItems]) => ({
        category,
        items: groupItems.sort((x, y) =>
          String(x.name || "").localeCompare(String(y.name || ""), "pt-BR", {
            sensitivity: "base",
          }),
        ),
      }));
  }, [items]);

  const toggle = (id) => {
    const idStr = String(id);
    const current = new Set((selectedAdditionalIds || []).map(String));
    if (current.has(idStr)) current.delete(idStr);
    else current.add(idStr);
    onChangeAdditionalIds(Array.from(current));
  };

  const [modal, setModal] = useState({ open: false, title: "", text: "" });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const selectedCount = selectedAdditionalIds?.length || 0;

  return (
    <div className="cqd2-addons cqd2-addons--full">
      {/* -- Cabecalho -- */}
      <div className="cqd2-addons__header">
        <div className="cqd2-addons__header-left">
          <div className="cqd2-addons__icon-wrap">
            <HiOutlineViewGrid />
          </div>
          <div>
            <div className="cqd2-addons__title">Complemente sua consulta</div>
            <div className="cqd2-addons__subtitle">
              Enriqueca sua consulta com dados adicionais
            </div>
          </div>
        </div>
        <div className="cqd2-addons__header-right">
          {selectedCount > 0 && (
            <span className="cqd2-addons__badge">
              {selectedCount}{" "}
              {selectedCount === 1 ? "selecionado" : "selecionados"}
            </span>
          )}
          <button
            type="button"
            className="cqd2-addons__toggle"
            aria-expanded={!isCollapsed}
            onClick={() => setIsCollapsed((prev) => !prev)}
          >
            {isCollapsed ? (
              <>
                <HiChevronRight />
                <span>Mostrar</span>
              </>
            ) : (
              <>
                <HiChevronDown />
                <span>Ocultar</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* -- Corpo -- */}
      {!isCollapsed && (
        <div className="cqd2-addons__body">
          {groupedItems.length === 0 && (
            <div className="cqd2__empty">Nenhum adicional disponivel.</div>
          )}

          {groupedItems.map((group) => (
            <div key={group.category} className="cqd2-addons__section">
              <div className="cqd2-addons__section-head">
                <span className="cqd2-addons__section-dot" />
                <span className="cqd2-addons__section-title">
                  {group.category}
                </span>
              </div>
              <div className="cqd2-addons__list">
                {group.items.map((opt) => {
                  const checked = selectedSet.has(String(opt.id));
                  const disabled = Boolean(opt.disabled);
                  return (
                    <div
                      key={opt.id}
                      className={`cqd2-addon ${checked ? "is-active" : ""} ${
                        disabled ? "is-disabled" : ""
                      }`}
                      role="checkbox"
                      aria-checked={checked}
                      aria-disabled={disabled}
                      tabIndex={disabled ? -1 : 0}
                      onClick={() => !disabled && toggle(opt.id)}
                      onKeyDown={(e) => {
                        if (disabled) return;
                        if (e.key === " " || e.key === "Enter") {
                          e.preventDefault();
                          toggle(opt.id);
                        }
                      }}
                    >
                      <span className="cqd2-addon__check">
                        {checked && <HiCheck />}
                      </span>
                      <span className="cqd2-addon__name">{opt.name}</span>
                      <button
                        type="button"
                        className="cqd2-addon__info-btn"
                        tabIndex={-1}
                        onClick={(e) => {
                          e.stopPropagation();
                          setModal({
                            open: true,
                            title: opt.name,
                            text:
                              opt.longDescription ||
                              "Sem descricao disponivel.",
                          });
                        }}
                        aria-label={`Detalhes de ${opt.name}`}
                      >
                        <HiOutlineInformationCircle />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <InfoModal
        open={modal.open}
        title={modal.title}
        text={modal.text}
        onClose={() => setModal({ open: false, title: "", text: "" })}
      />
    </div>
  );
}

function SummaryCard({ title, description, tags, hasDescription, hasTags }) {
  const fallback = [
    "Dados cadastrais",
    "Ocorrencias / pendencias",
    "Protestos e acoes",
    "Score e indicadores",
  ];

  return (
    <section className="cqd2-card">
      <div className="cqd2-card__header">
        <HiOutlineViewGrid />
        <span>Resumo</span>
      </div>
      <div className="cqd2-card__body cqd2-card__body--summary">
        {hasDescription ? (
          <div className="cqd2__desc">
            <div className="cqd2__desc-title">{title}</div>
            <p>Descricao: {description.trim()}</p>
          </div>
        ) : (
          <div className="cqd2__empty cqd2__empty--rich">
            <div className="cqd2__desc-title">{title}</div>
            <p className="cqd2__empty-text">
              Aproveite as informacoes para tomar decisoes rapidas e confiaveis
              enquanto entregamos insights com base no melhor sistema de
              consultas do mercado.
            </p>
          </div>
        )}

        {/* {hasTags && (
          <>
            <div className="cqd2__divider">O que retorna</div>
            <TagCloud tags={tags} max={12} />
          </>
        )} */}
      </div>
    </section>
  );
}

function InfoModal({ open, title, text, onClose }) {
  if (!open) return null;
  return (
    <div className="cqd2-modal" role="dialog" aria-modal="true">
      <div className="cqd2-modal__backdrop" onClick={onClose} aria-hidden />
      <div className="cqd2-modal__content">
        <div className="cqd2-modal__header">
          <h4>{title}</h4>
          <button onClick={onClose} aria-label="Fechar">
            x
          </button>
        </div>
        <div className="cqd2-modal__body">
          <p>{text || "Sem descricao disponivel."}</p>
        </div>
        <div className="cqd2-modal__footer">
          <button onClick={onClose}>Entendi</button>
        </div>
      </div>
    </div>
  );
}

function TagCloud({ tags = [], max = 12 }) {
  const [expanded, setExpanded] = useState(false);

  const sorted = useMemo(() => {
    const arr = (Array.isArray(tags) ? tags : []).map((t) =>
      typeof t === "string" ? { name: t } : t || {},
    );
    const seen = new Set();
    const unique = [];
    for (const t of arr) {
      const key = String(t.name ?? "")
        .trim()
        .toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      unique.push(t);
    }
    return unique.sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), "pt-BR", {
        sensitivity: "base",
        numeric: true,
      }),
    );
  }, [tags]);

  const visible = expanded ? sorted : sorted.slice(0, max);
  const hidden = Math.max(sorted.length - visible.length, 0);

  return (
    <div className="cqd2-tags">
      <ul>
        {visible.map((item, i) => (
          <li key={i}>
            <span className="cqd2-tag">
              <span className="cqd2__dot" />
              {item.name}
            </span>
          </li>
        ))}
      </ul>
      {hidden > 0 && !expanded && (
        <button className="cqd2-tag__more" onClick={() => setExpanded(true)}>
          +{hidden}
        </button>
      )}
      {expanded && sorted.length > max && (
        <button className="cqd2-tag__more" onClick={() => setExpanded(false)}>
          Mostrar menos
        </button>
      )}
    </div>
  );
}
